import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Mail, Phone, User, Tag, FileText, Loader2 } from "lucide-react";
import api from "../../lib/api";

/**
 * Brief 3.4.2 — minimal /admin/prospects/{prospect_id} detail page.
 *
 * Founder lands here when clicking a prospect row in /admin/pipeline
 * that has no merchant_id yet (otherwise the click goes to the unified
 * merchant page). Read-only view: status, contact info, notes, status
 * history.
 *
 * NOT in 3.4.2 scope: in-page status edit (use Nerea in Ops DM:
 * "Update Atlas to qualifying"), prospect creation form (use Nerea
 * "Track new prospect: ..."). Those flows are intentionally pushed
 * through the brain so the audit trail captures the operator intent.
 */

const STATUS_LABELS = {
  interested: "Interested",
  discovery: "Discovery",
  qualifying: "Qualifying",
  ready_for_onboarding: "Ready for onboarding",
  onboarding_link_sent: "Onboarding link sent",
  converted_to_merchant: "Converted",
  lost: "Lost",
};

function StatusPill({ status }) {
  const tone = {
    interested:           "bg-zinc-500/[0.08] text-zinc-300 border-zinc-500/30",
    discovery:            "bg-sky-500/[0.10] text-sky-200 border-sky-500/30",
    qualifying:           "bg-violet-500/[0.10] text-violet-200 border-violet-500/30",
    ready_for_onboarding: "bg-amber-400/[0.10] text-amber-200 border-amber-400/30",
    onboarding_link_sent: "bg-emerald-400/[0.10] text-emerald-200 border-emerald-400/30",
    converted_to_merchant:"bg-emerald-400/[0.10] text-emerald-200 border-emerald-400/30",
    lost:                 "bg-rose-400/[0.08] text-rose-200 border-rose-400/30",
  }[status] || "bg-white/[0.04] text-zinc-400 border-white/[0.08]";
  return (
    <span
      data-testid={`prospect-detail-status-${status}`}
      className={`inline-flex items-center rounded-full border uppercase tracking-[0.14em] text-[10.5px] px-2.5 py-1 ${tone}`}
    >
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function AdminProspectDetail() {
  const { prospectId } = useParams();
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/admin/prospects/${prospectId}`);
        if (!cancelled) setProspect(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status === 404 ? "Prospect not found." : "Could not load prospect.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [prospectId]);

  if (loading) {
    return (
      <div data-testid="prospect-detail-loading" className="text-zinc-500 inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em]">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading prospect…
      </div>
    );
  }
  if (error || !prospect) {
    return (
      <div data-testid="prospect-detail-error" className="rounded-2xl border border-rose-400/20 bg-rose-400/[0.04] p-5 text-rose-200 text-[13px] max-w-xl">
        {error || "Prospect not found."}
        <div className="mt-3">
          <Link to="/admin/pipeline" className="text-[11px] uppercase tracking-[0.18em] text-rose-200 hover:text-white">
            <ArrowLeft className="inline h-3 w-3 mr-1" /> Back to pipeline
          </Link>
        </div>
      </div>
    );
  }

  const contact = prospect.primary_contact || {};
  const history = prospect.status_history || [];

  return (
    <div data-testid="prospect-detail-page" className="max-w-3xl">
      <Link
        to="/admin/pipeline"
        data-testid="prospect-detail-back"
        className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-zinc-500 hover:text-white"
      >
        <ArrowLeft className="h-3 w-3" /> Pipeline
      </Link>

      <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Prospect</div>
          <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter text-white">
            {prospect.name}
          </h1>
          <div className="mt-2 flex items-center gap-3">
            <StatusPill status={prospect.status} />
            {prospect.vertical && (
              <span className="inline-flex items-center gap-1.5 text-[11.5px] text-zinc-400">
                <Tag className="h-3 w-3" /> {prospect.vertical}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Identity / contact card */}
      <section
        data-testid="prospect-detail-identity"
        className="mt-6 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5"
      >
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Primary contact</div>
        {contact.name || contact.email || contact.has_phone ? (
          <ul className="space-y-2 text-[13px] text-zinc-200">
            {contact.name && (
              <li className="flex items-center gap-2">
                <User className="h-3.5 w-3.5 text-zinc-500" /> {contact.name}
                {contact.role && <span className="text-zinc-500 text-[11.5px]">· {contact.role}</span>}
              </li>
            )}
            {contact.email && (
              <li className="flex items-center gap-2">
                <Mail className="h-3.5 w-3.5 text-zinc-500" />
                <a href={`mailto:${contact.email}`} className="hover:text-white">{contact.email}</a>
              </li>
            )}
            {contact.has_phone && (
              <li className="flex items-center gap-2 text-zinc-400">
                <Phone className="h-3.5 w-3.5 text-zinc-500" />
                <span className="text-[11.5px]">phone on file (admin reveal flow ships in 3.4.6)</span>
              </li>
            )}
          </ul>
        ) : (
          <div className="text-zinc-600 text-[12.5px]">No contact info yet.</div>
        )}

        {prospect.merchant_id && (
          <div className="mt-4 pt-4 border-t border-white/[0.04]">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1">Linked merchant</div>
            <Link
              to={`/admin/merchants/${prospect.merchant_id}`}
              data-testid="prospect-detail-linked-merchant"
              className="text-[12.5px] text-emerald-300 hover:text-white ref-mono"
            >
              {prospect.merchant_id}
            </Link>
          </div>
        )}
      </section>

      {/* Notes */}
      {prospect.notes && (
        <section
          data-testid="prospect-detail-notes"
          className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5"
        >
          <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-2">
            <FileText className="h-3 w-3" /> Notes
          </div>
          <p className="text-[13px] text-zinc-300 whitespace-pre-wrap">{prospect.notes}</p>
        </section>
      )}

      {/* Lost reason if applicable */}
      {prospect.lost_reason && (
        <section
          data-testid="prospect-detail-lost"
          className="mt-5 rounded-2xl border border-rose-400/20 bg-rose-400/[0.04] p-5"
        >
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-rose-200 mb-2">Lost reason</div>
          <p className="text-[13px] text-rose-100 whitespace-pre-wrap">{prospect.lost_reason.reason}</p>
          <div className="mt-2 text-[11px] text-rose-200/60">
            By {prospect.lost_reason.lost_by} · {new Date(prospect.lost_reason.lost_at).toLocaleString()}
          </div>
        </section>
      )}

      {/* Status history */}
      <section
        data-testid="prospect-detail-history"
        className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5"
      >
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-3">Status history</div>
        {history.length === 0 ? (
          <div className="text-zinc-600 text-[12.5px]">No transitions recorded.</div>
        ) : (
          <ol className="space-y-2 text-[12.5px]">
            {history.slice().reverse().map((h, i) => (
              <li key={i} className="flex items-baseline gap-3">
                <span className="text-zinc-500 text-[11px] ref-mono shrink-0 w-44">
                  {new Date(h.timestamp).toLocaleString()}
                </span>
                <span className="text-zinc-200">
                  {h.from_status ? `${h.from_status} → ` : ""}
                  <span className="text-white">{h.to_status}</span>
                </span>
                {h.actor_email && (
                  <span className="text-zinc-600 text-[11px]">· by {h.actor_email}</span>
                )}
                {h.via && (
                  <span className="text-zinc-700 text-[10.5px] ref-mono">· {h.via}</span>
                )}
              </li>
            ))}
          </ol>
        )}
      </section>

      <div className="mt-5 text-[11.5px] text-zinc-600">
        Update via Nerea in Ops DM: <span className="ref-mono text-zinc-400">Update {prospect.name} to qualifying</span>
      </div>
    </div>
  );
}
